import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import logo from '../assets/logo.svg'; // Assuming your logo is here

const VerificationContainer = styled.div`
  max-width: 600px;
  margin: 5rem auto;
  padding: 2.5rem;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const CompanyLogo = styled.img`
  max-width: 180px;
  margin-bottom: 2rem;
`;

const StatusIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: ${({ success }) => (success ? '#28a745' : '#d9534f')};
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: #555;
  line-height: 1.6;
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 2rem;
  text-align: left;
  border-top: 1px solid #e0e0e0;
  padding-top: 2rem;
`;

const DetailItem = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  font-size: 1rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const DetailValue = styled.span`
  color: #555;
`;

const SpinningIcon = styled(FaSpinner)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CompanyInfo = styled.div`
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
`;

const VerificationPage = () => {
  const { verificationId } = useParams();
  const [verificationStatus, setVerificationStatus] = useState('loading');
  const [verificationData, setVerificationData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!verificationId) {
      setVerificationStatus('error');
      setError('No verification ID provided.');
      return;
    }

    const verifyLetter = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/verify-letter/${verificationId}`);
        const result = await response.json();

        if (response.ok && result.success) {
          setVerificationStatus('success');
          setVerificationData(result.data);
        } else {
          setVerificationStatus('error');
          setError(result.error || 'Verification failed.');
        }
      } catch (err) {
        setVerificationStatus('error');
        setError('Could not connect to the server.');
      }
    };

    verifyLetter();
  }, [verificationId]);

  const renderSuccess = () => {
    if (!verificationData) return null;

    const issuedDate = new Date(verificationData.issuedAt);
    const expiryDate = new Date(issuedDate);
    expiryDate.setDate(expiryDate.getDate() + 90);

    return (
      <>
        <CompanyLogo src={logo} alt="The Letter Doctor Logo" />
        <StatusIcon success><FaCheckCircle /></StatusIcon>
        <Title>Certificate Verified</Title>
        <Message>This is an authentic document issued by The Letter Doctor.</Message>
        <DetailsList>
          <DetailItem><DetailLabel>Order Number:</DetailLabel><DetailValue>{verificationData.orderId}</DetailValue></DetailItem>
          <DetailItem><DetailLabel>Date Issued:</DetailLabel><DetailValue>{issuedDate.toLocaleDateString('en-GB')}</DetailValue></DetailItem>
          <DetailItem><DetailLabel>Expires On:</DetailLabel><DetailValue>{expiryDate.toLocaleDateString('en-GB')}</DetailValue></DetailItem>
        </DetailsList>
        <CompanyInfo>
          <strong>The Letter Doctor</strong><br />
          105 Talbot Road, Talbot Green, CF72 8AE<br />
          Email: admin@theletterdoctor.co.uk | Website: www.theletterdoctor.co.uk
        </CompanyInfo>
      </>
    );
  };

  return (
    <VerificationContainer>
      {verificationStatus === 'loading' && (
        <><StatusIcon><SpinningIcon /></StatusIcon><Title>Verifying Letter...</Title><Message>Please wait while we confirm the authenticity of this document.</Message></>
      )}

      {verificationStatus === 'success' && renderSuccess()}

      {verificationStatus === 'error' && (
        <><StatusIcon><FaTimesCircle /></StatusIcon><Title>Verification Failed</Title><Message>{error}</Message></>
      )}
    </VerificationContainer>
  );
};

export default VerificationPage;